import {
  PromptInput,
  PromptInputAttachment,
  PromptInputAttachments,
  PromptInputBody,
  PromptInputButton,
  PromptInputFooter,
  PromptInputSubmit,
  PromptInputTextarea,
  PromptInputTools,
  usePromptInputAttachments,
  type PromptInputMessage,
} from "@/components/ai-elements/prompt-input";
import { MicIcon, PaperclipIcon } from "lucide-react";
import React, { useCallback } from "react";
import { toast } from "sonner";

const MAX_FILE_SIZE = 10 * 1024 * 1024; // 10MB
const ACCEPTED_IMAGE_TYPES = ["image/jpeg", "image/png", "image/webp", "image/gif"];

interface PromptInputConciergeProps {
  placeholder?: string;
  onSubmit: (text: string, imageData?: string, imageMimeType?: string) => void;
  disabled?: boolean;
  value?: string;
  onChange?: (value: string) => void;
  onAttachClick?: () => void;
  onMicClick?: () => void;
  isStreaming?: boolean;
}

export const PromptInputConcierge: React.FC<PromptInputConciergeProps> = ({
  placeholder = "Ask anything",
  onSubmit,
  disabled = false,
  isStreaming = false,
  value,
  onChange,
}) => {
  const handleSubmit = useCallback(
    async (message: PromptInputMessage) => {
      if (!message.text?.trim() && (!message.files || message.files.length === 0)) {
        return;
      }

      // Handle image attachment
      let imageData: string | undefined;
      let imageMimeType: string | undefined;

      if (message.files && message.files.length > 0) {
        const file = message.files[0]; // Only handle first image for now

        // Validate file type
        if (!ACCEPTED_IMAGE_TYPES.includes(file.mediaType || "")) {
          toast.error("Please upload a valid image file (JPEG, PNG, WebP, or GIF)");
          return;
        }

        imageMimeType = file.mediaType;

        // Extract base64 data from data URL or blob URL
        if (file.url) {
          if (file.url.startsWith("data:")) {
            // Data URL - extract base64 by splitting (faster than regex for large strings)
            const dataUrlPrefix = "data:";
            const base64Prefix = ";base64,";

            try {
              if (file.url.includes(base64Prefix)) {
                const mimeTypeEnd = file.url.indexOf(base64Prefix);
                imageMimeType = file.url.substring(dataUrlPrefix.length, mimeTypeEnd);
                imageData = file.url.substring(mimeTypeEnd + base64Prefix.length);
              } else {
                console.error("Data URL doesn't contain base64 marker");
              }
            } catch (error) {
              console.error("Failed to extract base64 from data URL:", error);
            }
          } else if (file.url.startsWith("blob:")) {
            // Blob URL - fetch and convert to base64
            try {
              const response = await fetch(file.url);
              const blob = await response.blob();
              const reader = new FileReader();

              imageData = await new Promise<string>((resolve, reject) => {
                reader.onloadend = () => {
                  const result = reader.result as string;
                  // Extract base64 data from data URL
                  const base64Match = result.match(/^data:[^;]+;base64,(.+)$/);
                  if (base64Match) {
                    resolve(base64Match[1]);
                  } else {
                    reject(new Error("Failed to extract base64 data"));
                  }
                };
                reader.onerror = reject;
                reader.readAsDataURL(blob);
              });
            } catch (error) {
              console.error("Failed to read blob URL:", error);
              toast.error("Failed to read image file");
              return;
            }
          }
        }
      }

      // Submit with image data if present
      onSubmit(message.text || "", imageData, imageMimeType);
    },
    [onSubmit]
  );

  const handleError = useCallback((err: { code: string; message: string }) => {
    if (err.code === "max_file_size") {
      toast.error("File size exceeds 10MB limit");
    } else if (err.code === "accept") {
      toast.error("Please upload a valid image file");
    } else {
      toast.error(err.message);
    }
  }, []);

  const status = isStreaming ? "streaming" : "ready";

  return (
    <div className="tw:mx-auto tw:w-full tw:max-w-[900px]">
      <PromptInput
        onSubmit={handleSubmit}
        multiple={false}
        globalDrop
        accept="image/*"
        maxFiles={1}
        maxFileSize={MAX_FILE_SIZE}
        onError={handleError}
      >
        <PromptInputBody>
          <PromptInputAttachments>
            {(attachment) => <PromptInputAttachment data={attachment} />}
          </PromptInputAttachments>
          <PromptInputTextarea
            placeholder={placeholder}
            disabled={disabled}
            value={value}
            onChange={(e) => onChange?.(e.target.value)}
            rows={1}
            className="min-h-10! max-h-48 resize-none"
          />
        </PromptInputBody>
        <PromptInputFooter>
          <PromptInputTools>
            {/* Attachment button for image upload */}
            <AttachmentButton disabled={disabled || isStreaming} />
            {/* Disabled until we support audio input*/}
            <PromptInputButton disabled>
              <MicIcon size={16} />
            </PromptInputButton>
          </PromptInputTools>
          <PromptInputSubmit disabled={disabled || isStreaming} status={status} />
        </PromptInputFooter>
      </PromptInput>
    </div>
  );
};

// Separate component to use the hook inside PromptInput context
const AttachmentButton: React.FC<{ disabled: boolean }> = ({ disabled }) => {
  const attachments = usePromptInputAttachments();

  return (
    <PromptInputButton
      disabled={disabled}
      onClick={() => attachments.openFileDialog()}
    >
      <PaperclipIcon size={16} />
    </PromptInputButton>
  );
};
